import type { CpuStat, HistoryRange, Overview, ResourceStat, ResourceStatus } from "../../shared/api.js";
import { Page } from "../Page.js";
import { Dialog } from "../components/Dialog.js";
import { favoriteStar, favoritesFirst } from "../components/Favorite.js";
import { openResource, runOut } from "../components/ResourceDialog.js";
import { stackIcon } from "../components/StackIcon.js";
import { StackPicker } from "../components/StackPicker.js";
import { ViewToggle } from "../components/ViewToggle.js";
import { debounce, h, patch } from "../dom.js";
import { ago, amount, duration, perHour, stackName } from "../format.js";

const PAGE_SIZE = 60;
const DAY = 86_400_000;

const STATUS_LABEL: Record<ResourceStatus, string> = {
  tracked: "Monitored",
  ok: "OK",
  low: "Low",
  crafting: "Crafting",
  backoff: "Backing off",
  disabled: "Disabled",
  unknown: "No data",
};

type Sort = "runout" | "use" | "prod" | "fill" | "name";

const SORTS: Record<Sort, { label: string; cmp: (a: ResourceStat, b: ResourceStat) => number }> = {
  runout: {
    label: "Runs out soonest",
    cmp: (a, b) => (a.rates?.emptyAt ?? Infinity) - (b.rates?.emptyAt ?? Infinity) || fill(a) - fill(b),
  },
  use: { label: "Most used", cmp: (a, b) => (b.rates?.usePerHour ?? -1) - (a.rates?.usePerHour ?? -1) },
  prod: { label: "Most produced", cmp: (a, b) => (b.rates?.prodPerHour ?? -1) - (a.rates?.prodPerHour ?? -1) },
  fill: { label: "Emptiest against min", cmp: (a, b) => fill(a) - fill(b) },
  name: { label: "Name", cmp: (a, b) => stackName(a.stack).localeCompare(stackName(b.stack)) },
};

function fill(r: ResourceStat): number {
  // Resources without a rule have no min to be empty against.
  return r.stock === null || r.ruleId === null ? Infinity : r.stock / Math.max(1, r.min);
}

/**
 * The page the dashboard opens on: every monitored resource (each rule, and
 * anything in the network picked with "Monitor a resource") with what is left
 * and how fast it moves, as cards or a list with the favorites first, then a
 * card per crafting CPU with how much it is used.
 */
export class HomePage extends Page {
  readonly title = "Home";
  private data: Overview | null = null;
  private shown = PAGE_SIZE;

  private readonly search = h("input", { type: "search", placeholder: "Search resources…" });
  private readonly type = h("select", null, h("option", { value: "" }, "All types"), h("option", { value: "item" }, "Items"), h("option", { value: "fluid" }, "Fluids"), h("option", { value: "essentia" }, "Essentia"));
  private readonly sort = h("select", null, ...Object.entries(SORTS).map(([v, s]) => h("option", { value: v }, `Sort: ${s.label.toLowerCase()}`)));
  private readonly range = h("select", { title: "Window the rates are measured over" }, ...(["1h", "6h", "24h", "7d"] as const).map((r) => h("option", { value: r, selected: r === "6h" }, `Rates over ${r}`)));
  private readonly summary = h("p", { class: "muted" });
  private readonly resources = h("div");
  private readonly cpus = h("div");
  private readonly view = new ViewToggle("home", () => this.renderResources());

  mount(): void {
    const rerender = debounce(() => {
      this.shown = PAGE_SIZE;
      this.renderResources();
    }, 150);
    this.search.addEventListener("input", rerender);
    this.type.addEventListener("change", rerender);
    this.sort.addEventListener("change", rerender);
    this.range.addEventListener("change", () => void this.load());
    this.el.append(
      h("h2", null, "Monitored resources"),
      this.summary,
      h(
        "div",
        { class: "toolbar" },
        this.search,
        this.type,
        this.sort,
        this.range,
        h("span", { class: "spacer" }),
        this.view.el,
        h("button", { type: "button", class: "primary", title: "Chart anything the network stores, with or without a rule", onclick: () => this.openTrack() }, "Monitor a resource"),
      ),
      this.resources,
      h("h2", null, "Crafting CPUs"),
      this.cpus,
    );
    this.listen("stock", debounce(() => this.applyLiveStock(), 500));
    const reloadSoon = debounce(() => void this.load(), 2000);
    this.listen("cpus", () => {
      this.renderCpus();
      reloadSoon(); // picks up the new busy times
    });
    this.listen("rules", debounce(() => void this.load(), 500));
    this.listen("tracked", debounce(() => void this.load(), 300));
    // Re-pin straight away; the reload adds or drops favorites that have no rule.
    const reloadFavorites = debounce(() => void this.load(), 800);
    this.listen("favorites", () => {
      this.renderResources();
      reloadFavorites();
    });
    this.listen("network", debounce(() => void this.load(), 1000));
    this.every(60_000, () => void this.load());
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      this.data = await this.ctx.api.overview(this.range.value as HistoryRange);
    } catch (err) {
      this.summary.textContent = (err as Error).message;
      return;
    }
    this.applyLiveStock(false);
    this.renderResources();
    this.renderCpus();
  }

  private applyLiveStock(render = true): void {
    if (!this.data) return;
    const live = this.ctx.live.stock;
    for (const r of this.data.resources) {
      const v = live.get(r.key);
      if (v !== undefined) r.stock = v;
    }
    if (render) this.renderResources();
  }

  private renderResources(): void {
    const all = this.data?.resources ?? [];
    const low = all.filter((r) => r.ruleId !== null && r.stock !== null && r.stock < r.min).length;
    const soon = all.filter((r) => r.rates?.emptyAt && r.rates.emptyAt - Date.now() < DAY).length;
    this.summary.textContent = all.length
      ? `${all.length} monitored, ${low} below min, ${soon} running out within a day at the current rate.`
      : "Nothing monitored yet. Monitor any resource the network stores, or add a rule on the Stock page to keep it stocked.";
    const q = this.search.value.trim().toLowerCase();
    const type = this.type.value;
    const favorites = this.ctx.live.favorites;
    const rows = favoritesFirst(
      all
        .filter((r) => (!type || r.stack.type === type) && (!q || `${stackName(r.stack)} ${r.key}`.toLowerCase().includes(q)))
        .sort(SORTS[this.sort.value as Sort].cmp),
      (r) => favorites.has(r.key),
    );
    if (!all.length) {
      patch(this.resources);
      return;
    }
    if (!rows.length) {
      patch(this.resources, h("p", { class: "muted empty" }, "Nothing matches."));
      return;
    }
    const more = h("button", { onclick: () => ((this.shown += PAGE_SIZE), this.renderResources()) }, "Show more");
    const shown = rows.slice(0, this.shown);
    patch(this.resources, 
      this.view.cards
        ? h("div", { class: "mini-grid" }, ...shown.map((r) => this.resourceCard(r)))
        : h(
            "div",
            { class: "table-wrap" },
            h(
              "table",
              { class: "resources" },
              h(
                "thead",
                null,
                h("tr", null, h("th", null, "Resource"), h("th", { class: "stock-col" }, "Left"), h("th", { class: "num" }, "Produced"), h("th", { class: "num" }, "Used"), h("th", { class: "num" }, "Net"), h("th", null, "Runs out"), h("th", null, "Status")),
              ),
              h("tbody", null, ...shown.map((r) => this.resourceRow(r))),
            ),
          ),
      ...(rows.length > this.shown ? [h("div", { class: "table-foot" }, h("span", { class: "muted small" }, `Showing ${this.shown} of ${rows.length}`), more)] : []),
    );
  }

  /** A mini card: picture, name, what is left against the rule, net rate and when it runs out. */
  private resourceCard(r: ResourceStat): HTMLElement {
    const type = r.stack.type;
    const rates = r.rates;
    const soon = rates?.emptyAt ? rates.emptyAt - Date.now() < DAY : false;
    const low = r.ruleId !== null && r.stock !== null && r.stock < r.min;
    const scale = Math.max(r.target, r.min, 1);
    const pct = r.stock === null ? 0 : Math.min(100, (r.stock / scale) * 100);
    const open = () => openResource(this.ctx, r.key, r.stack);
    return h(
      "article",
      {
        key: r.key,
        class: `mini-card clickable status-${r.status}${this.ctx.live.favorites.has(r.key) ? " favorite" : ""}`,
        tabindex: 0,
        title: `${stackName(r.stack)}: show the chart`,
        onclick: open,
        onkeydown: (e: KeyboardEvent) => e.key === "Enter" && open(),
      },
      h("div", { class: "mc-head" }, h("span", { class: "slot" }, stackIcon(r.stack, "lg")), h("span", { class: "mc-name" }, stackName(r.stack)), favoriteStar(this.ctx, r.key, r.stack)),
      h(
        "div",
        { class: "mc-amount" },
        h("strong", { class: low ? "error" : "" }, amount(r.stock, type)),
        r.ruleId === null ? null : h("span", { class: "muted small" }, ` / ${amount(r.target, type)}`),
      ),
      r.ruleId === null
        ? h("div", { class: "bar ghost" })
        : h("div", { class: "bar" }, h("div", { class: `fill ${low ? "low" : ""}`, style: `width:${pct}%` }), h("div", { class: "min-mark", style: `left:${(r.min / scale) * 100}%` })),
      h(
        "div",
        { class: "mc-foot" },
        h("span", { class: `badge ${r.status}` }, STATUS_LABEL[r.status]),
        rates
          ? h("span", { class: `small ${rates.netPerHour < 0 ? "warn" : "muted"}`, title: `Net rate. Produced ${perHour(rates.prodPerHour, type, false)}, used ${perHour(rates.usePerHour, type, false)}` }, perHour(rates.netPerHour, type))
          : null,
      ),
      h("div", { class: `small ${soon ? "warn" : "muted"}` }, runOut(rates, r.stock)),
    );
  }

  private resourceRow(r: ResourceStat): HTMLElement {
    const type = r.stack.type;
    const scale = Math.max(r.target, r.min, 1);
    const pct = r.stock === null ? 0 : Math.min(100, (r.stock / scale) * 100);
    const rates = r.rates;
    const soon = rates?.emptyAt ? rates.emptyAt - Date.now() < DAY : false;
    return h(
      "tr",
      { key: r.key, class: "clickable", tabindex: 0, title: "Show the chart", onclick: () => openResource(this.ctx, r.key, r.stack), onkeydown: (e: KeyboardEvent) => e.key === "Enter" && openResource(this.ctx, r.key, r.stack) },
      h("td", null, h("div", { class: "stack-name" }, favoriteStar(this.ctx, r.key, r.stack), stackIcon(r.stack), stackName(r.stack))),
      r.ruleId === null
        ? h("td", { class: "stock-cell" }, h("span", { class: "stock-text" }, amount(r.stock, type)))
        : h(
            "td",
            { class: "stock-cell" },
            h("span", { class: "stock-text" }, `${amount(r.stock, type)} `, h("span", { class: "muted small" }, `/ ${amount(r.target, type)}`)),
            h("div", { class: "bar" }, h("div", { class: `fill ${r.stock !== null && r.stock < r.min ? "low" : ""}`, style: `width:${pct}%` }), h("div", { class: "min-mark", style: `left:${(r.min / scale) * 100}%` })),
          ),
      h("td", { class: "num" }, rates ? perHour(rates.prodPerHour, type, false) : "–"),
      h("td", { class: "num" }, rates ? perHour(rates.usePerHour, type, false) : "–"),
      h("td", { class: "num" }, rates ? perHour(rates.netPerHour, type) : "–"),
      h("td", { class: soon ? "warn" : "muted" }, runOut(rates, r.stock)),
      h("td", null, h("span", { class: `badge ${r.status}` }, STATUS_LABEL[r.status])),
    );
  }

  /** Picks anything the network stores and monitors it without a rule. */
  private openTrack(): void {
    const dialog = new Dialog("Monitor a resource", true);
    const add = h("button", { type: "button", class: "primary", disabled: true }, "Monitor");
    const picker = new StackPicker(this.ctx, (stack) => (add.disabled = !stack), "network");
    add.addEventListener("click", async () => {
      const stack = picker.value;
      if (!stack) return;
      add.disabled = true;
      const done = await this.attempt(() => this.ctx.api.track(stack), `Monitoring ${stackName(stack)}`);
      if (done) {
        dialog.close();
        void this.load();
      } else add.disabled = false;
    });
    dialog.body.append(
      h("p", { class: "muted small" }, "Its stock is read and charted like a rule's, but it is never crafted. Add a rule on the Stock page to keep it stocked."),
      picker.el,
    );
    dialog.footer.append(h("button", { type: "button", onclick: () => dialog.close() }, "Cancel"), add);
    dialog.open();
    picker.focus();
  }

  private renderCpus(): void {
    // Busy state and output come live; the use figures come from the last overview.
    const stats = new Map((this.data?.cpus ?? []).map((c) => [c.name, c]));
    const cpus = this.ctx.live.cpus;
    if (!cpus.length) {
      patch(this.cpus, h("p", { class: "muted empty" }, this.ctx.live.link?.connected ? "The Butler has not reported any crafting CPU." : "The game is not connected."));
      return;
    }
    const card = (name: string, busy: boolean, s: CpuStat | undefined, output: CpuStat["output"]) =>
      h(
        "article",
        { key: name, class: `cpu-card ${busy ? "busy" : "free"}` },
        h("header", null, h("strong", null, name.startsWith("#") ? `Unnamed CPU ${name}` : name), h("span", { class: `badge ${busy ? "crafting" : "ok"}` }, busy ? "Busy" : "Free")),
        output
          ? h("div", { class: "cpu-output" }, h("span", { class: "slot" }, stackIcon(output.stack, "lg")), h("strong", null, `${amount(output.amount, output.stack.type)} × ${stackName(output.stack)}`))
          : h("div", { class: "cpu-output muted" }, h("span", { class: "slot" }), busy ? "Crafting" : "Idle"),
        h(
          "dl",
          { class: "cpu-stats" },
          h("dt", null, "Busy for"),
          h("dd", null, busy && s?.busySince ? duration(Date.now() - s.busySince) : "–"),
          h("dt", null, "Average craft"),
          h("dd", null, s?.avgCraftMs ? duration(s.avgCraftMs) : "–"),
          h("dt", null, "Crafts, 7 days"),
          h("dd", null, String(s?.crafts ?? 0)),
          h("dt", null, "Last used"),
          h("dd", null, busy ? "now" : ago(s?.lastUsedAt ?? null)),
        ),
        h(
          "div",
          { class: "cpu-share", title: "Share of the last 24 h this CPU was busy" },
          h("div", { class: "small muted" }, `Busy ${Math.round((s?.busyShare24h ?? 0) * 100)}% of the last 24 h`),
          h("div", { class: "bar" }, h("div", { class: "fill busy", style: `width:${(s?.busyShare24h ?? 0) * 100}%` })),
        ),
      );
    patch(this.cpus, h("div", { class: "cpu-grid" }, ...cpus.map((c) => card(c.name, c.busy, stats.get(c.name), c.output))));
  }
}
